import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const StatusChangeModal = ({ isOpen, onClose, onSave, user, loading }) => {
  const [formData, setFormData] = useState({
    status: 'active',
    remarks: ''
  });
  const [errors, setErrors] = useState({});

  // Predefined remarks for inactive status
  const inactiveRemarks = [
    'Account suspended by admin',
    'User requested deactivation',
    'Policy violation',
    'Security concern',
    'Duplicate account',
    'Contract terminated',
    'User no longer active',
    'Data integrity issue',
    'Compliance requirement',
    'Other reason'
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        status: user.status || 'active',
        remarks: user.remarks || ''
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    if (formData.status === 'inactive' && !formData.remarks) {
      newErrors.remarks = 'Remarks are required when setting user as inactive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      status: 'active',
      remarks: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Change User Status</h2>
            <p className="text-sm text-text-secondary mt-1">
              Update status for {user.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Current Status Info */}
            <div className="p-4 bg-secondary-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-text-primary">Current Status</div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'active' 
                        ? 'bg-success-100 text-success-800' 
                        : 'bg-error-100 text-error-800'
                    }`}>
                      {user.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    {user.status === 'inactive' && user.remarks && (
                      <span className="text-xs text-secondary-600">
                        ({user.remarks})
                      </span>
                    )}
                  </div>
                </div>
                <Icon 
                  name={user.status === 'active' ? 'CheckCircle' : 'XCircle'} 
                  size={20} 
                  className={user.status === 'active' ? 'text-success' : 'text-error'} 
                />
              </div>
            </div>

            {/* New Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-text-primary mb-2">
                New Status *
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-150 ${
                  errors.status ? 'border-error' : 'border-secondary-300'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && (
                <p className="text-error text-sm mt-1">{errors.status}</p>
              )}
            </div>

            {/* Remarks (Required for Inactive) */}
            {formData.status === 'inactive' && (
              <div>
                <label htmlFor="remarks" className="block text-sm font-medium text-text-primary mb-2">
                  Reason for Deactivation *
                </label>
                <select
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-150 ${
                    errors.remarks ? 'border-error' : 'border-secondary-300'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select reason...</option>
                  {inactiveRemarks.map((remark, index) => (
                    <option key={index} value={remark}>
                      {remark}
                    </option>
                  ))}
                </select>
                {errors.remarks && (
                  <p className="text-error text-sm mt-1">{errors.remarks}</p>
                )}
                
                <div className="mt-2 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning-600 mt-0.5" />
                    <div className="text-sm text-warning-800">
                      <strong>Important:</strong> Inactive users will not be able to log in to the system.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Remarks (Optional for Active) */}
            {formData.status === 'active' && (
              <div>
                <label htmlFor="remarks" className="block text-sm font-medium text-text-primary mb-2">
                  Remarks (Optional)
                </label>
                <input
                  type="text"
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="Optional remarks for status change..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-150 border-secondary-300 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
              </div>
            )}

            {/* User Info */}
            <div className="pt-4 border-t border-secondary-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-secondary-600">Email:</span>
                  <div className="font-medium text-text-primary">{user.email}</div>
                </div>
                <div>
                  <span className="text-secondary-600">Role:</span>
                  <div className="font-medium text-text-primary capitalize">{user.role}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 bg-secondary-50">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center space-x-2 px-4 py-2 text-white rounded-md focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                formData.status === 'active' 
                  ? 'bg-success hover:bg-success-700' 
                  : 'bg-error hover:bg-error-700'
              }`}
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              <span>
                {loading 
                  ? 'Updating...' 
                  : `Set as ${formData.status === 'active' ? 'Active' : 'Inactive'}`
                }
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StatusChangeModal; 